/**
 * The trainer console.
 *
 * Not a page you scroll. A thin command bar states who and when, and
 * three columns below it hold the day's work — each one scrolling
 * inside its own frame so the whole picture stays on screen at once.
 * Nothing falls below the fold, because there is no fold: the page
 * itself does not scroll.
 *
 *   TODAY      what you are teaching, and what is next
 *   NEEDS YOU  the students and batches that will not fix themselves
 *   SIGNALS    the trend behind both
 *
 * Below 1200px the columns stack and the page scrolls normally — a
 * three-column console on a phone is a worse phone page.
 */

import { useEffect, useRef, useState } from 'react';
import {
  ArrowRight,
  CalendarClock,
  CalendarDays,
  Clock,
  Flame,
  MapPin,
  Radio,
  TriangleAlert,
  Users,
  Video,
} from 'lucide-react';

import { Donut, DonutLegend, TrendChart } from './charts';
import { OutreachCard } from './OutreachCards';
import { formatDate, formatTime, timelineProgress } from './useTrainerDashboard';

/**
 * The console is sized to the space it actually has, not to a guessed
 * viewport fraction: the vendor layout puts a header and padding above
 * it, so it measures its own top edge and claims what is left.
 */
function useFillHeight(enabled) {
  const ref = useRef(null);
  const [height, setHeight] = useState(null);

  useEffect(() => {
    if (!enabled) { setHeight(null); return undefined; }

    const measure = () => {
      const el = ref.current;
      if (!el) return;
      if (window.innerWidth <= 1200) { setHeight(null); return; }
      const top = el.getBoundingClientRect().top;
      // 24px so the last column does not sit flush against the window edge.
      setHeight(Math.max(520, window.innerHeight - top - 24));
    };

    measure();
    window.addEventListener('resize', measure);
    const t = setTimeout(measure, 120); // after fonts settle the bar's height is final
    return () => {
      window.removeEventListener('resize', measure);
      clearTimeout(t);
    };
  }, [enabled]);

  return [ref, height];
}

const rateTone = (r) =>
  r === null || r === undefined ? 'sky' : r >= 85 ? 'good' : r >= 60 ? 'warn' : 'bad';

/* ── Column frame: sticky head with a count, scrolling body ── */
function Column({ icon, title, count, countTone = 'sky', note, children }) {
  return (
    <section className="td-col">
      <header className="td-col-head">
        <span className="td-col-icon">{icon}</span>
        <h3>{title}</h3>
        {count !== undefined && (
          <span className={`td-col-count td-tone-${countTone}`}>{count}</span>
        )}
        {note && <span className="td-col-note">{note}</span>}
      </header>
      <div className="td-col-body">{children}</div>
    </section>
  );
}

const Divider = ({ children }) => <div className="td-col-divider">{children}</div>;

const Nothing = ({ icon, children }) => (
  <div className="td-col-empty">
    {icon}
    <p>{children}</p>
  </div>
);

export default function Console({ trainerName, trainer, stats, insights, view, onOpenTab }) {
  const [ref, height] = useFillHeight(true);
  const today = new Date();

  const attendanceRate = insights?.attendance?.rate ?? 0;
  const atRisk = insights?.students?.atRiskList ?? [];
  const ratedCount = insights?.students?.rated ?? 0;
  const trend = insights?.attendanceTrend ?? [];
  const mix = (insights?.studentMix ?? []).map((m) => ({ name: m.name, value: m.students }));
  const mixTotal = mix.reduce((n, m) => n + m.value, 0);

  const name = trainerName || trainer?.name || 'Trainer';
  const initials = name.trim().split(/\s+/).slice(0, 2).map((w) => w[0]).join('').toUpperCase();

  // Running batches that have never had a session marked. Every student in
  // them reads as absent, so this is a register problem before it is an
  // attendance problem.
  const unmarked = view.active.filter((b) => insights?.batchStats?.[String(b.id)]?.sessions === 0);

  const nextUp = [...view.upcoming]
    .sort((a, b) => new Date(a.startTime) - new Date(b.startTime))
    .slice(0, 4);

  return (
    <div className="td-console" ref={ref} style={height ? { height } : undefined}>
      {/* ═══ COMMAND BAR ═══ */}
      <header className="td-cmdbar">
        <span className="td-cmd-avatar">{initials || 'T'}</span>

        <div className="td-cmd-id">
          <h1>{name}</h1>
          <p>
            <CalendarDays size={12} />
            {today.toLocaleDateString('en-IN', {
              weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
            })}
          </p>
        </div>

        <div className="td-cmd-figs">
          <div className={`td-cmd-fig${attendanceRate < 60 ? ' bad' : attendanceRate < 75 ? ' warn' : ''}`}>
            <b>{attendanceRate}%</b>
            <span>attendance</span>
          </div>
          <div className="td-cmd-fig">
            <b>{view.active.length}</b>
            <span>running</span>
          </div>
          <div className="td-cmd-fig">
            <b>{view.todayClasses.length}</b>
            <span>today</span>
          </div>
          <div className="td-cmd-fig">
            <b>{stats?.totalStudents || 0}</b>
            <span>students</span>
          </div>
          <div className={`td-cmd-fig${atRisk.length ? ' bad' : ''}`}>
            <b>{atRisk.length}</b>
            <span>at risk</span>
          </div>
        </div>
      </header>

      {/* ═══ WORKSPACE ═══ */}
      <div className="td-cols">
        {/* ─────────── TODAY ─────────── */}
        <Column
          icon={<Clock size={15} />}
          title="Today"
          count={view.todayClasses.length}
          countTone={view.todayClasses.length ? 'good' : 'sky'}
        >
          {view.todayClasses.length === 0 ? (
            <Nothing icon={<CalendarClock size={30} />}>
              None of your running batches meet today. A good day to mark any
              sessions you still owe.
            </Nothing>
          ) : (
            view.todayClasses.map((b) => {
              const s = insights?.batchStats?.[String(b.id)];
              const room = b.classroomName && b.classroomName !== 'Not assigned'
                && b.classroomName !== 'Unknown room' ? b.classroomName : null;
              return (
                <article className="td-class" key={b.id || b._id}>
                  <div className="td-class-time">
                    <b>{formatTime(b.startTime)}</b>
                    <span>{formatTime(b.endTime)}</span>
                  </div>
                  <div className="td-class-main">
                    <h4>{b.name}</h4>
                    <p className="td-class-meta">{b.courseName}</p>
                    <div className="td-class-tags">
                      <span className="td-chip"><Users size={11} /> {b.studentCount ?? s?.enrolled ?? 0}</span>
                      {room && <span className="td-chip"><MapPin size={11} /> {room}</span>}
                      {b.mode && <span className="td-chip">{b.mode}</span>}
                    </div>

                    {s?.syllabusProgress !== null && s?.syllabusProgress !== undefined && (
                      <div className="td-class-bars">
                        <span className="td-bar"><i>Syllabus</i>
                          <em><u style={{ width: `${s.syllabusProgress}%` }} className="warn" /></em>
                          <b>{s.syllabusProgress}%</b>
                        </span>
                        <span className="td-bar"><i>Timeline</i>
                          <em><u style={{ width: `${s.elapsedProgress}%` }} /></em>
                          <b>{s.elapsedProgress}%</b>
                        </span>
                      </div>
                    )}

                    {b.meetingLink && (
                      <a className="td-dbtn primary td-class-join" href={b.meetingLink}
                        target="_blank" rel="noopener noreferrer">
                        <Video size={14} /> Join class
                      </a>
                    )}
                  </div>
                </article>
              );
            })
          )}

          {nextUp.length > 0 && (
            <>
              <Divider>Starting soon</Divider>
              {nextUp.map((b) => (
                <div className="td-mini" key={b.id || b._id}>
                  <div className="td-mini-main">
                    <b>{b.name}</b>
                    <span>{b.courseName}</span>
                  </div>
                  <span className="td-chip">{formatDate(b.startTime)}</span>
                </div>
              ))}
            </>
          )}
        </Column>

        {/* ─────────── NEEDS YOU ─────────── */}
        <Column
          icon={<Flame size={15} />}
          title="Needs you"
          count={atRisk.length + view.attention.length + unmarked.length}
          countTone={atRisk.length + view.attention.length + unmarked.length ? 'bad' : 'good'}
          note={ratedCount ? `${ratedCount} rated` : undefined}
        >
          {unmarked.length > 0 && (
            <div className="td-warnstrip">
              <TriangleAlert size={15} />
              <span>
                <b>{unmarked.length} running batch{unmarked.length === 1 ? '' : 'es'}</b> have no session
                marked at all. Every student in them reads as absent until you mark one.
              </span>
            </div>
          )}

          {atRisk.length === 0 && view.attention.length === 0 ? (
            <Nothing icon={<Users size={30} />}>
              {ratedCount
                ? 'Nobody is under 60% and no batch is off track. Nothing to chase.'
                : 'No attendance marked yet, so nobody has a rate to fall below.'}
            </Nothing>
          ) : (
            atRisk.map((s) => (
              <OutreachCard key={s.id} student={s} trainerName={name} compact />
            ))
          )}

          {view.attention.length > 0 && (
            <>
              <Divider>Batches off track</Divider>
              {view.attention.map(({ batch, reasons }) => (
                <div className="td-mini risk" key={batch.id}>
                  <div className="td-mini-main">
                    <b>{batch.name}</b>
                    <span>{reasons.map((r) => r.label).join(' · ')}</span>
                  </div>
                  <button type="button" className="td-mini-go" onClick={() => onOpenTab('batches')}
                    aria-label={`Open ${batch.name}`}>
                    <ArrowRight size={14} />
                  </button>
                </div>
              ))}
            </>
          )}
        </Column>

        {/* ─────────── SIGNALS ─────────── */}
        <Column
          icon={<Radio size={15} />}
          title="Signals"
          note="last 12 weeks"
        >
          <div className="td-sig">
            <h5>Attendance over time</h5>
            {trend.some((t) => t.rate !== null) ? (
              <>
                <TrendChart data={trend} height={168} />
                <p className="td-sig-note">A gap means no session was held that week — not a drop to zero.</p>
              </>
            ) : (
              <Nothing icon={<CalendarDays size={26} />}>No sessions marked in the last 12 weeks.</Nothing>
            )}
          </div>

          <div className="td-sig">
            <h5>Where your students are</h5>
            {mix.length ? (
              <div className="td-donut-row">
                <Donut data={mix} total={mixTotal} caption="STUDENTS" size={150} />
                <DonutLegend data={mix} />
              </div>
            ) : (
              <Nothing icon={<Users size={26} />}>No students in a running batch.</Nothing>
            )}
          </div>

          <div className="td-sig">
            <h5>Where you stand</h5>
            <div className="td-class-bars wide">
              <span className="td-bar"><i>Attendance</i>
                <em><u style={{ width: `${attendanceRate}%` }}
                  className={rateTone(attendanceRate)} /></em>
                <b>{attendanceRate}%</b>
              </span>
              <span className="td-bar"><i>Performance</i>
                <em><u style={{ width: `${insights?.performance?.score ?? 0}%` }} /></em>
                <b>{insights?.performance?.score ?? '—'}</b>
              </span>
              <span className="td-bar"><i>Timeline</i>
                <em><u style={{
                  width: `${view.active.length
                    ? Math.round(view.active.reduce((n, b) => n + timelineProgress(b.startTime, b.endTime), 0) / view.active.length)
                    : 0}%`,
                }} /></em>
                <b>{view.active.length
                  ? Math.round(view.active.reduce((n, b) => n + timelineProgress(b.startTime, b.endTime), 0) / view.active.length)
                  : 0}%</b>
              </span>
            </div>
            <p className="td-sig-note">
              {insights?.attendance?.presentMarks ?? 0} present of{' '}
              {insights?.attendance?.expectedMarks ?? 0} expected, across{' '}
              {insights?.attendance?.sessionsConducted ?? 0} marked sessions.
            </p>
          </div>

          {insights?.courses?.list?.length > 0 && (
            <div className="td-sig">
              <h5>What you teach</h5>
              {insights.courses.list.map((c) => (
                <div className="td-mini" key={c.id}>
                  <div className="td-mini-main">
                    <b>{c.name}</b>
                    <span>{c.batches} batch{c.batches === 1 ? '' : 'es'}
                      {c.activeBatches > 0 && ` · ${c.activeBatches} running`}</span>
                  </div>
                  <span className="td-chip">{c.students}</span>
                </div>
              ))}
            </div>
          )}
        </Column>
      </div>
    </div>
  );
}
